import { Combobox, SelectItem } from 'uikit';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useMemo } from 'react';

export interface IHierarchyViewSelectItem {
    id: number;
    parentId?: number;
    label: string;
    name?: string;
}

interface IHierarchyViewSelectInternal extends IHierarchyViewSelectItem {
    hasChildren: boolean;
    level: number;
    disabled?: boolean;
}

interface IHierarchyViewSelect {
    items: IHierarchyViewSelectItem[];
    onlyLeafsSelectable?: boolean;
    disabled?: boolean;
    selectedItem?: IHierarchyViewSelectItem;
    onChange: (selectedItem?: IHierarchyViewSelectItem) => void;
    placeholder?: string;
    rootId?: number;
    label?: string;
    style?: React.CSSProperties;
    labelPosition?: 'top' | 'left';
    size?: 's' | 'm' | 'l';
    className?: string;
    sortItemsById?: boolean;
}

const rootKey = 'root';

const getParentProp = (item: IHierarchyViewSelectItem) => (item.parentId ? String(item.parentId) : 'root');
const groupByParentId = R.groupBy(getParentProp);

export const flattenTree = (
    tree: Record<string, IHierarchyViewSelectItem[]>,
    parentId: string,
    onlyLeafsSelectable = false,
    level = 0,
) =>
    (tree[parentId] ?? [])
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .reduce((acc, item) => {
            const children = flattenTree(tree, String(item.id), onlyLeafsSelectable, level + 1);
            const hasChildren = !!children.length;

            acc.push({ ...item, level, hasChildren, disabled: onlyLeafsSelectable && hasChildren }, ...children);

            return acc;
        }, [] as IHierarchyViewSelectInternal[]);

const sortById = (items: IHierarchyViewSelectItem[]) => items.sort((a, b) => a.id - b.id);

export const HierarchyViewSelect: React.FC<IHierarchyViewSelect> = (props) => {
    const {
        items,
        onlyLeafsSelectable,
        disabled,
        selectedItem,
        onChange,
        placeholder,
        rootId,
        label,
        style,
        labelPosition,
        size = 's',
        className,
        sortItemsById,
    } = props;

    const actualItems = useMemo(
        () =>
            flattenTree(
                groupByParentId(sortItemsById ? sortById(items) : items),
                rootId ? String(rootId) : rootKey,
                onlyLeafsSelectable,
            ),
        [items, onlyLeafsSelectable, rootId, sortItemsById],
    );

    const actualSelectedItem = useMemo(
        () => selectedItem && actualItems.find((item) => item.id === selectedItem.id),
        [actualItems, selectedItem],
    );

    const onChangeLocal = useCallback(
        ({ value }) => {
            const changeValue = items.find((item) => item.id === value?.id);

            onChange(changeValue);
        },
        [items, onChange],
    );

    return (
        <Combobox
            className={className}
            style={style}
            label={label}
            title={label}
            labelPosition={labelPosition}
            placeholder={placeholder}
            items={actualItems}
            value={actualSelectedItem}
            onChange={onChangeLocal}
            size={size}
            disabled={!actualItems.length || disabled}
            renderItem={({ item, active, hovered, onClick, onMouseEnter }) => (
                <SelectItem
                    label={item.label}
                    active={active}
                    hovered={hovered}
                    multiple={false}
                    size={size}
                    indent="normal"
                    onClick={onClick}
                    onMouseEnter={onMouseEnter}
                    disabled={item.disabled}
                    style={{ padding: '4px 8px', paddingLeft: item.level * 16 + 8 }}
                />
            )}
        />
    );
};

HierarchyViewSelect.displayName = 'HierarchyViewSelect';
